import { observable } from 'mobx';
import { Instance, types } from 'mobx-state-tree';
import { IEntity, IEntityStore, ITemplate } from '../../../main/entities';
import { mg } from '../../../mg/mg';
import { toasterSvc } from '../../../services/ToasterSvc';
import { getOmnistore, IOmnistore } from '../../../stores/Omnistore';

const OWNER_ONLY = 'onlyTheOwnerCanDoThis';
const State = types.enumeration(['NONE', 'START', 'BRANCH', 'SUPPLIER', 'TEMPLATE']);

export const TemplDetailsStore = types.model('TemplDetailsStore', {
	state: types.optional(State, 'NONE'),
	isPending: false,
	branchId: 0,
	supplierId: 0,
	templateId: 0,
})
	.views(self => ({
		get _omnistore() : IOmnistore {
			return getOmnistore();
		},
	}))
	.views(self => ({
		get _relevantId() : number {
			switch (self.state) {
			case 'BRANCH': return self.branchId;
			case 'SUPPLIER': return self.supplierId;
			case 'TEMPLATE': return self.templateId;
			default: return 0;
			}
		},
		get _relevantRepo() : IEntityStore | undefined {
			const m = self._omnistore.multirepo;
			switch (self.state) {
			case 'BRANCH': return m.branchRepo;
			case 'SUPPLIER': return m.supplierRepo;
			case 'TEMPLATE': return m.templateRepo;
			default: return undefined;
			}
		},
	}))
	.views(self => ({
		get immutableEntity() : IEntity | undefined {
			const r = self._relevantRepo;
			return r && r.byId(self._relevantId);
		},
	}))
	.views(self => ({
		get observableEntity() : IEntity | undefined {
			const e = self.immutableEntity;
			return e ? observable.object(e, undefined, {deep: false}) : undefined;
		},
	}))
	.views(self => ({
		get isBranchOwner() {
			const ownUserId = self._omnistore.profileStore.ownUserId;
			const e = self._omnistore.multirepo.branchRepo.byId(self.branchId);
			return e && e.ownerId===ownUserId;
		},
		get isSupplierOwner() {
			const ownUserId = self._omnistore.profileStore.ownUserId;
			const e = self._omnistore.multirepo.supplierRepo.byId(self.supplierId);
			return e && e.ownerId===ownUserId;
		},
	}))
	.views(self => ({
		get mainView() : Array<[string, string]> {
			if (self.state !== 'START') return [];
			const m = self._omnistore.multirepo;
			const b = m.branchRepo.byId(self.branchId);
			const s = m.supplierRepo.byId(self.supplierId);
			const t = m.templateRepo.byId(self.templateId);
			if (!mg.assertPresent(b, 'missing branch')) return [];
			if (!mg.assertPresent(s, 'missing supplier')) return [];
			if (!mg.assertPresent(t, 'missing template')) return [];
			return [
				['BRANCH', b.name],
				['SUPPLIER', s.name],
				['TEMPLATE', t.customerNo],
			];
		},
		get isValid() {
			return true;
		},
		get globalReasonForReadonly() : string {
			switch (self.state) {
			case 'BRANCH': return self.isBranchOwner ? '' : OWNER_ONLY;
			case 'SUPPLIER': return self.isSupplierOwner ? '' : OWNER_ONLY;
			case 'TEMPLATE': return self.isBranchOwner && self.isSupplierOwner ? '' : OWNER_ONLY;
			}
			return '?';
		},
		get title() {
			return 'titleEdit' + mg.capitalize(self.state.toLowerCase());
		},
	}))
	.views(self => ({
		reasonForReadonly(name: string) : string {
			return self.globalReasonForReadonly;
		},
	}))
	.actions(self => ({
		reset() {
			self.isPending = false;
			self.state = 'NONE';
		},
	}))
	.actions(self => ({
		start(template: ITemplate) {
			self.branchId = template.branchId;
			self.supplierId = template.supplierId;
			self.templateId = template.id;
			self.state = 'START';
		},
		setState(state: Instance<typeof State>) {
			self.state = state;
		},
		cancel() {
			self.reset();
		},
		onMainViewClick(event: {currentTarget: any}) {
			const t = event.currentTarget;
			if (!mg.assertPresent(t, 'onMainViewClick', 'missing event.currentTarget')) return;
			if (!mg.assertPresent(t.name, 'onMainViewClick', 'missing event.currentTarget.name')) return;
			const name = t.name.toUpperCase();
			console.log(name);
			switch (name) {
			case 'BRANCH':
			case 'SUPPLIER':
			case 'TEMPLATE':
				self.state = name;
				return;
			}
			mg.assertPresent(undefined, 'onMainViewClick', 'illegal event.currentTarget.name', name);
		},
		accept() {
			if (self.isPending) return;
			const e = self.observableEntity;
			if (!e) return;
			const path = 'Update' + mg.capitalize(self.state.toLowerCase());
			self.isPending = true;
			self._omnistore.communicator.doPost(path, e)
				.then(toasterSvc.failure)
				.then(r => r.outputObject ? self._relevantRepo!.fetch('DIRECT') : r)
				.finally(self.reset);
		},
	}));
